import { JOB_STEPS } from "../data/stages";
import { daysSince } from "./format";

// Where the posting / apply form lives, for review-queue tags.
export function boardLabel(job) {
  const source = (job.source || "").trim();
  const known = {
    Greenhouse: "Greenhouse",
    Lever: "Lever",
    Ashby: "Ashby",
    Workday: "Workday",
    a16z: "a16z",
    Simplify: "Simplify",
    Remotive: "Remotive",
  };
  if (known[source]) return known[source];
  const url = `${job.applyUrl || ""} ${job.url || ""}`;
  if (/greenhouse\.io/i.test(url)) return "Greenhouse";
  if (/lever\.co/i.test(url)) return "Lever";
  if (/ashbyhq\.com/i.test(url)) return "Ashby";
  if (/myworkdayjobs\.com|workday\.com/i.test(url)) return "Workday";
  if (/speedrun-talent-network\.com/i.test(url)) return "a16z";
  if (source && !/^manual$/i.test(source)) return source;
  return "Company site";
}

// Prepared by Platypus (stored on the legacy `job.automator` field) and not applied to or dismissed yet.
export function awaitingReview(job) {
  return !!job.automator && job.stage === "found" && !job.steps?.applied;
}

// Platypus's review queue, best matches first.
export function reviewQueue(jobs) {
  return jobs.filter(awaitingReview).sort((a, b) => (b.automator.matchScore ?? 0) - (a.automator.matchScore ?? 0));
}

export function questionStats(job) {
  const questions = job.questions || [];
  const needsYou = questions.filter((q) => q.needsYou).length;
  return { total: questions.length, needsYou, answered: questions.length - needsYou };
}

export function isStepSkipped(job, step) {
  return step.id === "coverLetter" && !!job.noCoverLetter && !job.steps?.coverLetter;
}

export function stepsFor(job) {
  return JOB_STEPS.filter((s) => !isStepSkipped(job, s));
}

export function nextStep(job) {
  if (job.stage === "rejected" || job.stage === "accepted") return null;
  const applied = job.steps?.applied;
  return stepsFor(job).find((s) => !job.steps?.[s.id] && !(applied && s.beforeApply)) || null;
}

export function stepProgress(job) {
  const steps = stepsFor(job);
  const done = steps.filter((s) => job.steps?.[s.id]).length;
  return { done, total: steps.length };
}

// Builds the "Needs your attention" list for the dashboard, most urgent first.
export function attentionItems(jobs) {
  const items = [];
  jobs.forEach((job) => {
    if (job.stage === "interview" && job.interviewAt && new Date(job.interviewAt) > new Date()) {
      items.push({
        job,
        priority: 0,
        icon: "bi-calendar-event",
        color: "#0891b2",
        text: `Interview at ${job.company}`,
        detail: job.interviewAt,
        detailType: "date",
        cta: "Prepare",
        to: `/jobs/${job.id}`,
      });
    }
    if (job.stage === "found") {
      const step = nextStep(job);
      items.push({
        job,
        priority: 2,
        icon: "bi-send",
        color: "#36454f",
        text: `Apply to ${job.title} at ${job.company}`,
        detail: step ? `Next: ${step.label.toLowerCase()}` : "",
        cta: "Continue",
        to: `/jobs/${job.id}?tab=${step ? step.tab : "apply"}`,
      });
    }
    if (job.stage === "applied" && !job.steps?.emailed) {
      items.push({
        job,
        priority: 1,
        icon: "bi-envelope",
        color: "#d97706",
        text: `Email the recruiter at ${job.company}`,
        detail: `Applied ${daysSince(job.appliedAt)} days ago`,
        cta: "Find contact",
        to: `/jobs/${job.id}?tab=contacts`,
      });
    }
    if (job.stage === "applied" && job.steps?.emailed && daysSince(job.appliedAt) >= 10) {
      items.push({
        job,
        priority: 3,
        icon: "bi-arrow-repeat",
        color: "#708090",
        text: `No reply from ${job.company} yet`,
        detail: `${daysSince(job.appliedAt)} days since you applied. Consider a follow-up.`,
        cta: "Follow up",
        to: `/jobs/${job.id}?tab=contacts`,
      });
    }
  });
  return items.sort((a, b) => a.priority - b.priority);
}

// Applications submitted per week for the last `weeks` weeks, oldest first.
export function weeklyApplications(jobs, weeks = 8) {
  const weekMs = 7 * 86400000;
  const nowMs = Date.now();
  const buckets = Array.from({ length: weeks }, (_, i) => {
    const end = nowMs - (weeks - 1 - i) * weekMs;
    return { start: end - weekMs, end, count: 0 };
  });
  jobs.forEach((job) => {
    if (!job.appliedAt) return;
    const t = new Date(job.appliedAt).getTime();
    const bucket = buckets.find((b) => t > b.start && t <= b.end);
    if (bucket) bucket.count += 1;
  });
  return buckets;
}
